import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { styled } from "@mui/material/styles";

import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import ButtonBlue from "../../../../shared/general/ButtonBlue";

import { getEmployeePersonnelLevelChange, getEmployeeProfile } from "../../../../../../actions/employee";
import { getDayjsFormatWithTranslate } from "../../../../../../utils";

import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DrawerEditEmployeePersonnelLevel from "./DrawerEditEmployeePersonnelLevel";
import DialogConfirmDelete from "../../../../shared/general/DialogConfirmDelete";
import { deleteEmployeePersonnelLevel } from "../../../../../../actions/level";
import { openNotificationAlert } from "../../../../../../actions/notificationAlert";

const StyledHeadLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
});

const StyledWrapTop = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  marginBottom: 16,
  "& .MuiTypography-body2": {
    margin: 0,
  },
});

const StyledDivider = styled(Divider)({
  marginTop: 16,
  marginBottom: 0,
  borderWidth: "0px 0px thin",
  borderColor: "#919eab52",
  borderStyle: "dashed",
  width: "100%",
});

const Promotion = () => {

  const { result: employeeProfile } = useSelector((state) => state.employeeProfile);
  const { result: employeePersonnelLevelChange } = useSelector((state) => state.employeePersonnelLevelChange);

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [drawerEditEmployeePersonnelLevel, setDrawerEditEmployeePersonnelLevel] = useState({
    isOpen: false,
    isEdit: false,
    data: {},
  })

  const [dialogDeleteConfig, setDialogDeleteConfig] = useState({
    isOpen: false,
    data: {}
  })

  const handleOpenAddDrawerEditPersonnelLevel = () => {
    setDrawerEditEmployeePersonnelLevel(prev => ({
      ...prev,
      isOpen: true,
      isEdit: false,
      data: {},
    }))
  }

  const handleOpenEditDrawerEditEmployeePersonnelLevel = (item) => {
    setDrawerEditEmployeePersonnelLevel(prev => ({
      ...prev,
      isOpen: true,
      isEdit: true,
      data: {
        idEmployeePersonnelLevel: item.idEmployeePersonnelLevel,
      },
    }))
  }

  const handleCloseDrawerEditEmployeePersonneLevel = () => {
    setDrawerEditEmployeePersonnelLevel(prev => ({
      ...prev,
      isOpen: false,
    }))
  }

  const handleOpenDialogDelete = (item) => {
    setDialogDeleteConfig(prev => ({
      ...prev,
      isOpen: true,
      data: {
        idEmployeePersonnelLevel: item.idEmployeePersonnelLevel,
      }
    }))
  }

  const handleCloseDialogDelete = (item) => {
    setDialogDeleteConfig(prev => ({
      ...prev,
      isOpen: false,
    }))
  }

  const handleDeleteEmployeePersonnelLevel = async () => {
    if(dialogDeleteConfig.data.idEmployeePersonnelLevel) {
      const result = await dispatch(deleteEmployeePersonnelLevel(dialogDeleteConfig.data.idEmployeePersonnelLevel));

      if(result && result.status === 200){
        if(!result.data.success){
          dispatch(openNotificationAlert({
            type: "error",
            message: result.data.message,
          }));
        } else {
          dispatch(openNotificationAlert({
            type: "success",
            message: t("DataSaveSuccessful"),
          }));
          dispatch(getEmployeeProfile(employeeProfile.idEmp));
          dispatch(getEmployeePersonnelLevelChange(employeeProfile.idEmp));
          handleCloseDialogDelete();
        }
      } else if (result && result.data.message) {
        dispatch(openNotificationAlert({
          type: "error",
          message: result.data.message,
        }));
      } else {
        dispatch(openNotificationAlert({
          type: "error",
          message: t("AnErrorOccurred"),
        }));
      }
    }
  }

  useEffect(() => {
    dispatch(getEmployeePersonnelLevelChange(employeeProfile.idEmp))
  }, [])

  return (
    <div>
      <StyledWrapTop>
        <div>
          <ButtonBlue
            size="small"
            startIcon={<EditIcon />}
            onClick={handleOpenAddDrawerEditPersonnelLevel}
          >
            {t("Edit")}
          </ButtonBlue>
        </div>
      </StyledWrapTop>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <StyledHeadLabel color="text.secondary">
            {t("LevelGroup")} ({t("Current")})
          </StyledHeadLabel>
          <Typography variant="h6">
            {employeeProfile.personnelGroupLevelName
              ? employeeProfile.personnelGroupLevelName
              : "-"}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <StyledHeadLabel color="text.secondary">
            {t("Level")} ({t("Current")})
          </StyledHeadLabel>
          <Typography variant="h6">
            {employeeProfile.personnelLevelName
              ? employeeProfile.personnelLevelName
              : "-"}
          </Typography>
        </Grid>
      </Grid>
      <StyledDivider />
      <Box marginTop="16px" marginBottom="16px">
        <StyledHeadLabel color="text.secondary">
          {t("ChangeLog")}
        </StyledHeadLabel>
      </Box>

      <Grid container spacing={2}>
        {employeePersonnelLevelChange.length === 0 && (
          <Grid item xs={12}>
            <Typography fontSize="20px" color="text.secondary" align="center">{t("NoData")}</Typography>
          </Grid>
        )}
        {employeePersonnelLevelChange.length > 0 && (
          <Fragment>
            {employeePersonnelLevelChange.map((item, index) => (
              <Grid key={item.idEmployeePersonnelLevel} item xs={12}>

                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography fontSize="20px" fontWeight="600">{item.groupLevelName} : {item.levelName}</Typography>
                    <Typography fontSize="14px" fontWeight="500">
                      {item.end
                        ? `[${getDayjsFormatWithTranslate(item.start, "DD MMM YYYY", i18n.language)} - ${getDayjsFormatWithTranslate(item.end, "DD MMM YYYY", i18n.language)}]`
                        : `[${getDayjsFormatWithTranslate(item.start, "DD MMM YYYY", i18n.language)} - ${t("Current")}]`}
                    </Typography>
                  </Box>

                  {index === 0 && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        aria-label="edit"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDrawerEditEmployeePersonnelLevel(item);
                        }}
                      >
                        <EditNoteRoundedIcon fontSize="inherit" />
                      </IconButton>

                      <IconButton
                        aria-label="delete"
                        size="small"
                        onClick={() => {handleOpenDialogDelete(item)}}
                        disabled={employeePersonnelLevelChange.length === 1}
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            backgroundColor: "error.light",
                            color: "white",
                          },
                        }}
                      >
                        <DeleteOutlineRoundedIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {`${t("Description")}: ${item.remark || "-"}`}
                </Typography>
                
                {item.updateDate && (
                  <Box mt={1}>
                    <Typography variant="caption" color="text.disabled" display="block">
                      {`${t("UpdateDate")}: ${item.updateDate? getDayjsFormatWithTranslate(item.updateDate, "D MMM YYYY HH:mm", i18n.language): "-"}`}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" display="block">
                      {`${t("UpdateBy")}: ${item.updater? item.updater: "-"}`}
                    </Typography>
                  </Box>
                )}
                <StyledDivider />
              </Grid>
            ))}
          </Fragment>
        )}
      </Grid>

      <DrawerEditEmployeePersonnelLevel
        drawerConfig={drawerEditEmployeePersonnelLevel}
        handleClose={handleCloseDrawerEditEmployeePersonneLevel}
      />

      <DialogConfirmDelete
        open={dialogDeleteConfig.isOpen}
        handleClose={handleCloseDialogDelete}
        handleDelete={handleDeleteEmployeePersonnelLevel}
      />

    </div>
  );
}

export default Promotion;
